import React, { useState, useEffect, useMemo } from 'react'
import { getCompleteAvailability } from '../services/influxService'
import { useSensorData } from '../hooks/useSensorData'

// Import our new components following SOLID principles
import DashboardHeader from './DashboardHeader'
import DashboardControls from './DashboardControls'
import PCO2Chart from './PCO2Chart'
import InformationSections from './InformationSections'

export default function PublicDashboard() {
  // State management
  const [timeRange, setTimeRange] = useState('-24h')
  const [availableBuoys, setAvailableBuoys] = useState([])
  const [selectedBuoy, setSelectedBuoy] = useState('CT1003')
  const [isLoading, setIsLoading] = useState(false)

  // Fixed selections for public view - only pCO2 data
  const selectedSensors = useMemo(() => ({ [selectedBuoy]: ['procv_co2'] }), [selectedBuoy])
  const selectedFields = useMemo(() => ({ [selectedBuoy]: ['procv_co2:pCO2'] }), [selectedBuoy])

  // Get sensor data using the existing hook
  const { loading, all: rawData } = useSensorData(
    [selectedBuoy],
    selectedSensors,
    selectedFields,
    timeRange
  )

  // Load available buoys
  useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    
    ;(async () => {
      try {
        const completeData = await getCompleteAvailability(timeRange, ['CT1002', 'CT1003'])
        if (!cancelled) {
          setAvailableBuoys(completeData.availableBuoys)
          // Set default buoy if available
          if (completeData.availableBuoys.length > 0 && !completeData.availableBuoys.includes(selectedBuoy)) {
            setSelectedBuoy(completeData.availableBuoys[0])
          }
        }
      } catch (e) {
        console.error('Failed to load buoys:', e)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()
    
    return () => { cancelled = true }
  }, [timeRange, selectedBuoy])

  // Event handlers following Dependency Inversion Principle
  const handleTimeRangeChange = (e) => setTimeRange(e.target.value)
  const handleBuoyChange = (e) => setSelectedBuoy(e.target.value)

  return (
    <div style={{ padding: '2rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header Component - Single Responsibility */}
      <DashboardHeader />

      {/* Controls Component - Single Responsibility */}
      <DashboardControls 
        timeRange={timeRange}
        onTimeRangeChange={handleTimeRangeChange}
        selectedBuoy={selectedBuoy}
        onBuoyChange={handleBuoyChange}
        availableBuoys={availableBuoys}
      />

      {/* Climate Context Section */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ 
          margin: '0 0 1rem 0',
          color: '#2c3e50',
          textAlign: 'center'
        }}>
          Ocean Alkalinity Enhancement & Climate Research
        </h3>
        <p style={{ 
          lineHeight: '1.6', 
          marginBottom: '1rem',
          color: '#495057',
          fontSize: '1rem'
        }}>
          To avoid the worst impacts of climate change, we must cut emissions and remove carbon already in the atmosphere. 
          Ocean Alkalinity Enhancement (OAE) is a leading carbon removal approach that slightly increases seawater's alkalinity, 
          allowing the ocean to convert CO₂ into stable forms (bicarbonate and carbonate) for 10,000+ years.
        </p>
        <p style={{ 
          lineHeight: '1.6', 
          margin: '0',
          color: '#495057',
          fontSize: '1rem'
        }}>
          Research and data are critical to test OAE's effectiveness, safety, and affordability. Monitoring key ocean variables 
          helps ensure this promising solution can be scaled responsibly.
        </p>
        <div style={{
          textAlign: 'right',
          marginTop: '1rem',
          fontSize: '0.9rem',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          <em>(Source: <a 
            href="https://www.carbontosea.org/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#007bff', textDecoration: 'none' }}
          >
            Carbon to Sea
          </a>)</em>
        </div>
      </div>

      {/* Chart Component - Single Responsibility */}
      <PCO2Chart 
        rawData={rawData}
        selectedBuoy={selectedBuoy}
        loading={loading || isLoading}
      />

      {/* Information Sections Component - Open/Closed Principle */}
      <InformationSections />
    </div>
  )
}